
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { ProfileHeader } from "@/components/public-profile/ProfileHeader";
import { AboutSection } from "@/components/public-profile/AboutSection";
import { TrainingLocation } from "@/components/public-profile/TrainingLocation";
import { ContactCard } from "@/components/public-profile/ContactCard";
import { PricingCard } from "@/components/public-profile/PricingCard";
import { ShareProfile } from "@/components/public-profile/ShareProfile";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";

export const PublicProfile = () => {
  const { publicUrl } = useParams();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { language } = useLanguage();
  const { t } = useTranslations("publicProfile");

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('profiles')
          .select('first_name, last_name, avatar_url, gym_photo_url, about_me, specialties, training_types, instagram_url, facebook_url, experience_years, hourly_rate, show_prices, public_url')
          .eq('public_url', publicUrl)
          .single();

        if (error) throw error;
        setProfile(data);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError(language === 'pt' ? 'Perfil não encontrado' : 'Profile not found');
      } finally {
        setLoading(false);
      }
    };

    if (publicUrl) {
      fetchProfile();
    }
  }, [publicUrl, language]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-gray-100 to-white dark:from-gray-900 dark:to-gray-800 relative">
        <div 
          className="absolute inset-0 w-full h-full z-0" 
          style={{
            backgroundImage: 'radial-gradient(#0000001a 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}
        />
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-8 rounded-xl shadow-lg relative z-10">
          <Loader2 className="h-12 w-12 animate-spin text-gray-500 dark:text-gray-400" />
          <p className="mt-4 text-gray-500 dark:text-gray-400 font-medium">
            {language === 'pt' ? 'A carregar perfil...' : 'Loading profile...'}
          </p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-gray-100 to-white dark:from-gray-900 dark:to-gray-800 relative">
        <div 
          className="absolute inset-0 w-full h-full z-0" 
          style={{
            backgroundImage: 'radial-gradient(#0000001a 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}
        />
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-8 rounded-xl shadow-lg text-center relative z-10">
          <div className="bg-red-50 dark:bg-red-900/30 rounded-full p-4 mx-auto w-16 h-16 flex items-center justify-center mb-4">
            <span className="text-red-500 dark:text-red-400 text-2xl">!</span>
          </div>
          <h2 className="text-xl font-semibold mb-2 dark:text-white">
            {language === 'pt' ? 'Perfil não encontrado' : 'Profile not found'}
          </h2>
          <p className="text-muted-foreground dark:text-gray-400">
            {error || (language === 'pt' ? 'Não foi possível encontrar este perfil' : 'Could not find this profile')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-200 to-white dark:from-gray-900 dark:to-gray-800 pb-12 relative">
      <div 
        className="absolute inset-0 w-full h-full z-0" 
        style={{
          backgroundImage: 'radial-gradient(#0000001a 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}
      />
      <div className="container max-w-4xl mx-auto pt-8 px-4 sm:px-6 relative z-10">
        <div className="space-y-8 animate-fade-in">
          <ProfileHeader profile={profile} />
          <AboutSection aboutMe={profile.about_me} />
          <TrainingLocation gymPhotoUrl={profile.gym_photo_url} />
          
          {/* Share Profile Section */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-6 rounded-xl shadow-md">
            <h2 className="text-xl font-semibold mb-4 text-center text-gray-900 dark:text-white">
              {t("shareProfile")}
            </h2>
            <ShareProfile profileUrl={profile.public_url} />
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6">
            <ContactCard 
              instagramUrl={profile.instagram_url}
              facebookUrl={profile.facebook_url}
            />
            <PricingCard 
              showPrices={profile.show_prices}
              hourlyRate={profile.hourly_rate}
            />
          </div>
          <div className="text-center pt-8">
            <p className="text-sm text-muted-foreground dark:text-gray-400">
              {language === 'pt' ? 'Perfil criado com ' : 'Profile created with '}
              <span className="text-gray-700 dark:text-gray-300">TrainerFOLIO</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
